import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import React from "react"
import ReactDOM from "react-dom/client"
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"
import { ApiError } from "./api/client"
import { InstallPrompt } from "./components/install_prompt"
import { UpdateBanner } from "./components/update_banner"
import { ToastContainer } from "./components/ui"
import { DashboardLayout, RequirePermission, RequireRole } from "./layouts/dashboard"
import { AgentClientsPage } from "./pages/agent_clients"
import { AgentPerformancePage } from "./pages/agent_performance"
import { AgentPipelinePage } from "./pages/agent_pipeline"
import { AdminAgentPerformancePage } from "./pages/admin_agent_performance"
import { AuditLogsPage } from "./pages/audit_logs"
import { BorrowerApplicationsPage } from "./pages/borrower_applications"
import { ClientListPage } from "./pages/clients"
import { CollectionsPage } from "./pages/collections"
import { CompletedReviewsPage } from "./pages/completed_reviews"
import { DashboardPage } from "./pages/dashboard"
import { DevSmsPage } from "./pages/dev_sms"
import { DisbursementsPage } from "./pages/disbursements"
import { InteractionLogPage } from "./pages/interaction_log"
import { LoginPage } from "./pages/login"
import { NotificationsPage } from "./pages/notifications"
import { ReconciliationPage } from "./pages/reconciliation"
import { ReportsPage } from "./pages/reports"
import { SystemConfigPage } from "./pages/system_config"
import { SystemHealthPage } from "./pages/system_health"
import { TargetsPage } from "./pages/targets"
import { TicketsPage } from "./pages/tickets"
import { UsersPage } from "./pages/users"
import { VerificationQueuePage } from "./pages/verification"
import { AuthProvider, useAuth } from "./stores/auth"
import { ToastProvider } from "./stores/toast"
import "./styles/app.css"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.code === "LOCAL_SCHEMA_OUTDATED") {
          return false
        }

        return failureCount < 1
      },
    },
  },
})

function App() {
  function HomeRedirect() {
    const { user, isLoading } = useAuth()

    if (isLoading) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <p className="text-sm text-slate-500">Loading...</p>
        </div>
      )
    }

    if (!user) {
      return <Navigate replace to="/login" />
    }

    return <Navigate replace to={user.defaultPath ?? "/dashboard"} />
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <BrowserRouter>
            <ToastContainer />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/dev/sms" element={<DevSmsPage />} />
              <Route path="/" element={<DashboardLayout />}>
                <Route index element={<HomeRedirect />} />
                <Route
                  path="dashboard"
                  element={
                    <RequireRole
                      allowedRoles={[
                        "SUPER_ADMIN",
                        "ADMIN",
                        "OPS_MANAGER",
                        "AGENT",
                        "VERIFICATION_STAFF",
                      ]}
                    >
                      <DashboardPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="clients"
                  element={
                    <RequireRole allowedRoles={["SUPER_ADMIN", "ADMIN", "OPS_MANAGER"]}>
                      <ClientListPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="collections"
                  element={
                    <RequireRole allowedRoles={["SUPER_ADMIN", "ADMIN", "OPS_MANAGER"]}>
                      <CollectionsPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="users"
                  element={
                    <RequireRole allowedRoles={["SUPER_ADMIN", "ADMIN"]}>
                      <UsersPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="targets"
                  element={
                    <RequireRole allowedRoles={["SUPER_ADMIN", "ADMIN", "OPS_MANAGER"]}>
                      <TargetsPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="agent-performance"
                  element={
                    <RequireRole allowedRoles={["SUPER_ADMIN", "ADMIN", "OPS_MANAGER"]}>
                      <AdminAgentPerformancePage />
                    </RequireRole>
                  }
                />
                <Route
                  path="reconciliation"
                  element={
                    <RequireRole allowedRoles={["SUPER_ADMIN", "ADMIN", "OPS_MANAGER"]}>
                      <ReconciliationPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="reports"
                  element={
                    <RequireRole allowedRoles={["SUPER_ADMIN", "ADMIN", "OPS_MANAGER"]}>
                      <ReportsPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="disbursements"
                  element={
                    <RequireRole allowedRoles={["SUPER_ADMIN"]}>
                      <DisbursementsPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="agent/pipeline"
                  element={
                    <RequireRole allowedRoles={["AGENT"]}>
                      <AgentPipelinePage />
                    </RequireRole>
                  }
                />
                <Route
                  path="agent/clients"
                  element={
                    <RequireRole allowedRoles={["AGENT"]}>
                      <AgentClientsPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="agent/interactions"
                  element={
                    <RequireRole allowedRoles={["AGENT"]}>
                      <InteractionLogPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="agent/performance"
                  element={
                    <RequireRole allowedRoles={["AGENT"]}>
                      <AgentPerformancePage />
                    </RequireRole>
                  }
                />
                <Route
                  path="tickets"
                  element={
                    <RequireRole
                      allowedRoles={[
                        "SUPER_ADMIN",
                        "ADMIN",
                        "OPS_MANAGER",
                        "AGENT",
                        "VERIFICATION_STAFF",
                      ]}
                    >
                      <TicketsPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="notifications"
                  element={
                    <RequireRole
                      allowedRoles={[
                        "SUPER_ADMIN",
                        "ADMIN",
                        "OPS_MANAGER",
                        "AGENT",
                        "VERIFICATION_STAFF",
                      ]}
                    >
                      <NotificationsPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="system-config"
                  element={
                    <RequireRole allowedRoles={["SUPER_ADMIN"]}>
                      <SystemConfigPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="system-health"
                  element={
                    <RequireRole allowedRoles={["SUPER_ADMIN"]}>
                      <SystemHealthPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="audit-logs"
                  element={
                    <RequireRole allowedRoles={["SUPER_ADMIN"]}>
                      <AuditLogsPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="borrower-applications"
                  element={
                    <RequireRole
                      allowedRoles={["SUPER_ADMIN", "ADMIN", "OPS_MANAGER", "VERIFICATION_STAFF"]}
                    >
                      <BorrowerApplicationsPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="verification"
                  element={
                    <RequirePermission permission="canReviewKyc">
                      <VerificationQueuePage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="completed-reviews"
                  element={
                    <RequireRole
                      allowedRoles={["SUPER_ADMIN", "ADMIN", "OPS_MANAGER", "VERIFICATION_STAFF"]}
                    >
                      <CompletedReviewsPage />
                    </RequireRole>
                  }
                />
              </Route>
              <Route path="*" element={<HomeRedirect />} />
            </Routes>
            <div className="admin-pwa-stack">
              <UpdateBanner />
              <InstallPrompt />
            </div>
          </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}

if ("serviceWorker" in navigator) {
  if (import.meta.env.PROD) {
    window.addEventListener("load", async () => {
      try {
        const registration = await navigator.serviceWorker.register("/sw.js")

        if (registration.waiting) {
          window.dispatchEvent(new CustomEvent("sw-update-available"))
        }

        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing
          if (!newWorker) return

          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
              window.dispatchEvent(new CustomEvent("sw-update-available"))
            }
          })
        })

        let refreshing = false
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          if (!refreshing) {
            refreshing = true
            window.location.reload()
          }
        })
      } catch {
      }
    })
  } else {
    void navigator.serviceWorker.getRegistrations().then((registrations) =>
      Promise.all(registrations.map((registration) => registration.unregister())),
    )

    if ("caches" in window) {
      void caches.keys().then((keys) =>
        Promise.all(
          keys
            .filter((key) => key.startsWith("admin-app-") || key.startsWith("workbox-"))
            .map((key) => caches.delete(key)),
        ),
      )
    }
  }
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)
